import dayjs, { Dayjs } from "dayjs";

/**
 * Máscara para a taxa de cálculo de prêmio como valor decimal simples
 * (ex.: "0,120001"). Remove o "%", normaliza ponto para vírgula, mantém
 * apenas uma vírgula e tira os zeros à esquerda da parte inteira, de modo
 * que valores legados como "00,120001%" passam a exibir "0,120001".
 */
export function maskDecimal(value?: string): string {
  if (!value) return "";

  // Mantém apenas dígitos, vírgula e ponto; normaliza ponto -> vírgula.
  let v = String(value).replace(/%/g, "").replace(/\./g, ",").replace(/[^\d,]/g, "");
  if (!v) return "";

  // Mantém somente a primeira vírgula.
  const firstComma = v.indexOf(",");
  if (firstComma !== -1) {
    v = v.slice(0, firstComma + 1) + v.slice(firstComma + 1).replace(/,/g, "");
  }

  const hasComma = v.includes(",");
  const [intRaw, ...rest] = v.split(",");
  const decimalPart = rest.join("");
  const intPart = intRaw === "" ? "0" : String(parseInt(intRaw, 10) || 0);

  return hasComma ? `${intPart},${decimalPart}` : intPart;
}

/** Função responsável por criar uma máscara de % para números */
export function maskPercentage(value?: string): string {
  if (!value) return "";

  // Remove all non-digit characters
  const digits = value.replace(/\D/g, "");

  if (!digits) return "";

  // Special case: exactly "100" (no comma)
  if (digits === "100") {
    return "100%";
  }

  // Limit to 2 digits before the comma
  const intPart = digits.slice(0, 2);
  const decimalPart = digits.slice(2);

  if (decimalPart) {
    return `${intPart},${decimalPart}%`;
  } else {
    return `${intPart}%`;
  }
}

/** Função responsável por verificar e converter Date para dayjs */
export function parseDate(value?: Date): Dayjs | null {
  if (!value) return null;

  if (!dayjs(value).isValid()) return null;

  return dayjs(value);
}

export function formatCurrency(payload?: string) {
  if (!payload) return "";

  // Remove all non-digits
  const numeric = payload.replace(/\D/g, "");

  // Convert to number and divide by 100 to get cents
  const amount = Number(numeric) / 100;

  // Format using Intl
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Verdadeiro quando ambos os valores de moeda estão preenchidos e o mínimo é
 * maior que o máximo. Compara os dígitos (centavos), independente da formatação.
 */
export function isMinAboveMax(valorMinimo?: string, valorMaximo?: string): boolean {
  const min = valorMinimo?.replace(/\D/g, "");
  const max = valorMaximo?.replace(/\D/g, "");
  return !!min && !!max && Number(min) > Number(max);
}
